import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import Card from '../../components/ui/Card/Card';
import Button from '../../components/ui/Button/Button';
import Input from '../../components/ui/Input/Input';
import Select from '../../components/ui/Select/Select';
import Checkbox from '../../components/ui/Checkbox/Checkbox';
import Popover from '../../components/ui/Popover/Popover';
import Spinner from '../../components/ui/Spinner/Spinner';
import Switch from '../../components/ui/Switch/Switch';
import { getOrganizations } from '../../controllers/organizations';
import {
  getUserAccessRoles,
  createUser,
  updateUser,
  getAllFunctionalities,
  createUserAccess,
  getUserAccess,
  updateUserAccess as updateUserAccessAPI,
  getUserProfileById,
  getUserIncubationAccess,
  bulkSetUserIncubationAccess
} from '../../controllers/users';
import { getIncubations } from '../../controllers/incubations';
import { useToast } from '../../contexts/ToastContext';
import { useUser } from '../../contexts/UserContext';
import './AddUserScreen.css';

const FUNCTIONALITY_DESCRIPTIONS = {
  'Admin': 'Access to all functionalities. Ability to add and remove other Admins.',
  'Manage Dashboard': 'Create, edit, and manage Dashboard Users, Organizations, and User Access Roles.',
  'Create Incubations': 'Create new incubations on the dashboard.',
  'Add Participants': 'Add new participants to this incubation.',
  'Edit Participants': 'Edit participants in this incubation.',
  'View Webhook Details': 'View webhook details of this incubation.',
  'View Pet Details': 'View pet details for participants in this incubation.',
  'View Incubation Logs': 'View the activity log of this incubation.',
  'Download Incubation Data': 'Export participant data from this incubation.',
  'Edit Incubations': 'Edit the configuration of this incubation.',
  'Delete Incubations': 'Delete this incubation.'
};

const getDescription = (func) => FUNCTIONALITY_DESCRIPTIONS[func.display_name] || null;

const AddUserScreen = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const { showError, showSuccess } = useToast();
  const { isDashboardAdmin } = useUser();

  const mode = location.state?.mode || (id ? 'edit' : 'create');
  const finalMode = mode;
  const currentUserIsAdmin = isDashboardAdmin();

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    role: '',
    organization: '',
    globalFunctionalityIds: [],
    incubationAccessMap: {}
  });

  const [errors, setErrors] = useState({});
  const [organizations, setOrganizations] = useState([]);
  const [roles, setRoles] = useState([]);
  const [functionalities, setFunctionalities] = useState([]);
  const [incubations, setIncubations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [dashboardPermissionsEnabled, setDashboardPermissionsEnabled] = useState(false);

  const { globalFunctionalities, incubationFunctionalities, adminFunctionalityId } = useMemo(() => {
    const globals = functionalities.filter(f => f.category === 'global');
    const incs    = functionalities.filter(f => f.category === 'incubation');
    const admin   = functionalities.find(f => f.name === 'admin_dashboard');
    return {
      globalFunctionalities: globals,
      incubationFunctionalities: incs,
      adminFunctionalityId: admin ? String(admin.id) : null
    };
  }, [functionalities]);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const loadUserData = async () => {
      if (finalMode !== 'edit' || !id) return;
      if (organizations.length === 0 || roles.length === 0) return;

      try {
        setLoading(true);

        const response = await getUserProfileById({ userId: id });
        if (!response?.data?.success || !response.data.user) {
          showError('Failed to load user data. Please try again.');
          navigate('/home', { state: { activeSection: 'Dashboard Access' } });
          return;
        }

        const user = response.data.user;
        const organizationId = user.organization_id || user.organization?.id || user.organizationId;
        const roleId = user.user_access_role_id || user.role_id || user.user_access_role?.id || user.role?.id || user.roleId;

        const [accessResponse, incubationAccessResponse] = await Promise.all([
          getUserAccess({ user_profile_id: id }).catch(e => ({ data: { success: false, error: e.message } })),
          getUserIncubationAccess({ user_profile_id: id }).catch(e => ({ data: { success: false, error: e.message } }))
        ]);

        const globalIds = accessResponse?.data?.success
          ? (accessResponse.data.userAccess?.functionality_ids || []).map(String)
          : [];

        const incubationMap = {};
        if (incubationAccessResponse?.data?.success && incubationAccessResponse.data.incubation_access) {
          for (const [incId, entry] of Object.entries(incubationAccessResponse.data.incubation_access)) {
            incubationMap[incId] = (entry?.functionality_ids || []).map(String);
          }
        }

        setFormData({
          firstName: user.first_name || '',
          lastName: user.last_name || '',
          email: user.email || '',
          phone: user.phone_number || '',
          role: roleId ? String(roleId) : '',
          organization: organizationId ? String(organizationId) : '',
          globalFunctionalityIds: globalIds,
          incubationAccessMap: incubationMap
        });
        setDashboardPermissionsEnabled(globalIds.length > 0);
      } catch (error) {
        console.error('Error loading user data:', error);
        showError('Failed to load user data. Please try again.');
        navigate('/home', { state: { activeSection: 'Dashboard Access' } });
      } finally {
        setLoading(false);
      }
    };

    loadUserData();
  }, [id, finalMode, organizations, roles]);

  const fetchData = async () => {
    try {
      setLoading(true);

      const [orgResponse, rolesResponse, functionalitiesResponse, incubationsResponse] = await Promise.all([
        getOrganizations({ page: 1, limit: 1000 }),
        getUserAccessRoles(),
        getAllFunctionalities(),
        getIncubations()
      ]);

      setOrganizations(orgResponse?.data?.organizations || []);

      if (rolesResponse?.data?.success) {
        setRoles((rolesResponse.data.roles || []).filter(r => !r.is_archived));
      }

      if (functionalitiesResponse?.data?.success) {
        setFunctionalities(functionalitiesResponse.data.functionalities || []);
      } else {
        showError('Failed to load functionalities. Please try again.');
      }

      if (incubationsResponse?.data?.success) {
        setIncubations(incubationsResponse.data.incubations || []);
      } else {
        showError('Failed to load incubations. Please try again.');
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      showError('Failed to load some data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const toggleGlobalFunctionality = (funcId, checked) => {
    setFormData(prev => {
      const currentSet = new Set(prev.globalFunctionalityIds);
      const idStr = String(funcId);

      if (checked) {
        currentSet.add(idStr);
        if (idStr === adminFunctionalityId) {
          globalFunctionalities.forEach(f => currentSet.add(String(f.id)));
        }
      } else {
        currentSet.delete(idStr);
        if (idStr === adminFunctionalityId) {
          globalFunctionalities.forEach(f => currentSet.delete(String(f.id)));
        }
      }

      return { ...prev, globalFunctionalityIds: [...currentSet] };
    });
  };

  const handleToggleDashboardPermissions = (enabled) => {
    setDashboardPermissionsEnabled(enabled);
    if (!enabled) {
      setFormData(prev => ({ ...prev, globalFunctionalityIds: [] }));
    }
  };

  const toggleIncubation = (incubationId, checked) => {
    setFormData(prev => {
      const nextMap = { ...prev.incubationAccessMap };
      if (checked) {
        if (!nextMap[incubationId]) {
          const role = roles.find(r => String(r.id) === String(prev.role));
          const defaults = (role?.default_incubation_functionality_ids || []).map(String);
          nextMap[incubationId] = defaults;
        }
      } else {
        delete nextMap[incubationId];
      }
      return { ...prev, incubationAccessMap: nextMap };
    });
  };

  const toggleIncubationPermission = (incubationId, funcId, checked) => {
    setFormData(prev => {
      const nextMap = { ...prev.incubationAccessMap };
      const current = new Set((nextMap[incubationId] || []).map(String));
      if (checked) current.add(String(funcId));
      else current.delete(String(funcId));
      nextMap[incubationId] = [...current];
      return { ...prev, incubationAccessMap: nextMap };
    });
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim())  newErrors.lastName = 'Last name is required';
    if (!formData.email.trim())     newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Please enter a valid email address';
    if (!formData.role)          newErrors.role = 'Role is required';
    if (!formData.organization)  newErrors.organization = 'Organization is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setSubmitting(true);

      const userData = {
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        phone_number: formData.phone,
        user_access_role_id: formData.role,
        organization_id: formData.organization
      };
      if (finalMode === 'edit' && id) userData.userId = id;

      const userResponse = finalMode === 'edit'
        ? await updateUser(userData)
        : await createUser(userData);

      if (!userResponse?.data?.success) {
        const action = finalMode === 'edit' ? 'update' : 'create';
        const errMsg = userResponse?.data?.error || 'Unexpected response from server.';
        showError(`Failed to ${action} user: ${errMsg}`);
        return;
      }

      const userProfileId = finalMode === 'edit' ? id : userResponse.data.user.id.toString();

      const globalPayload = {
        user_profile_id: userProfileId,
        functionality_ids: formData.globalFunctionalityIds
      };

      if (finalMode === 'create') {
        const accessResp = await createUserAccess(globalPayload);
        if (!accessResp?.data?.success) {
          console.warn('User created but user access creation failed:', accessResp?.data?.error);
          showError(accessResp?.data?.error || 'User created, but failed to set dashboard permissions.');
        }
      } else {
        const updateResp = await updateUserAccessAPI(globalPayload).catch(() => null);
        const updateOk = updateResp?.data?.success;
        if (!updateOk) {
          const errText = updateResp?.data?.error || '';
          if (errText.includes('No user access entry found')) {
            const createResp = await createUserAccess(globalPayload);
            if (!createResp?.data?.success) {
              showError(createResp?.data?.error || 'Failed to create dashboard permissions.');
            }
          } else if (errText) {
            showError(errText);
          }
        }
      }

      const incubationAccessPayload = {};
      for (const [incId, funcIds] of Object.entries(formData.incubationAccessMap)) {
        incubationAccessPayload[incId] = { functionality_ids: funcIds };
      }

      const bulkResp = await bulkSetUserIncubationAccess({
        user_profile_id: userProfileId,
        incubation_access: incubationAccessPayload
      });

      if (!bulkResp?.data?.success) {
        console.warn('Per-incubation access save failed:', bulkResp?.data?.error);
        showError(bulkResp?.data?.error || 'Failed to save per-incubation permissions.');
      } else {
        const action = finalMode === 'edit' ? 'updated' : 'created';
        showSuccess(`User ${action} successfully!`);
      }

      navigate('/home', { state: { activeSection: 'Dashboard Access' } });
    } catch (error) {
      console.error(`Error ${finalMode === 'edit' ? 'updating' : 'creating'} user:`, error);
      const action = finalMode === 'edit' ? 'update' : 'create';
      showError(`Failed to ${action} user. Please try again.`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleBackClick = () => navigate(-1);

  const renderGlobalCheckbox = (func) => {
    const idStr = String(func.id);
    const isAdminRow = idStr === adminFunctionalityId;
    const checked = formData.globalFunctionalityIds.map(String).includes(idStr);
    const disabled = isAdminRow && !currentUserIsAdmin;
    const description = getDescription(func);

    const label = (
      <span
        className={`functionality-label${disabled ? ' disabled' : ''}`}
        onClick={disabled ? undefined : () => toggleGlobalFunctionality(idStr, !checked)}
      >
        {func.display_name}{disabled ? ' (admin-only)' : ''}
      </span>
    );

    return (
      <div key={func.id} className="functionality-checkbox">
        <div className="functionality-checkbox-content">
          <Checkbox
            checked={checked}
            disabled={disabled}
            onChange={(e) => toggleGlobalFunctionality(idStr, e.target.checked)}
            label=""
          />
          {description ? (
            <Popover content={<div className="popover-body">{description}</div>} trigger="hover" placement="top" className="small" offset={16}>
              {label}
            </Popover>
          ) : label}
        </div>
      </div>
    );
  };

  const renderIncubationPermissionCheckbox = (incubationId, func) => {
    const idStr = String(func.id);
    const selected = (formData.incubationAccessMap[incubationId] || []).map(String);
    const checked = selected.includes(idStr);
    const description = getDescription(func);
    const label = (
      <span
        className="functionality-label"
        onClick={() => toggleIncubationPermission(incubationId, idStr, !checked)}
      >
        {func.display_name}
      </span>
    );

    return (
      <div key={`${incubationId}_${func.id}`} className="functionality-checkbox">
        <div className="functionality-checkbox-content">
          <Checkbox
            checked={checked}
            onChange={(e) => toggleIncubationPermission(incubationId, idStr, e.target.checked)}
            label=""
          />
          {description ? (
            <Popover content={<div className="popover-body">{description}</div>} trigger="hover" placement="top" className="small" offset={16}>
              {label}
            </Popover>
          ) : label}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <Spinner size="large" />
        <p>Loading user data...</p>
      </div>
    );
  }

  return (
    <div className="add-user-screen">
      <button className="back-button-screen" onClick={handleBackClick}>
        <i className="fas fa-arrow-left"></i>
        <span>Back</span>
      </button>
      <div className="add-user-container">
        <Card style={{ width: '700px', margin: '0 auto', padding: '20px', overflow: 'visible' }}>
          <h2 className="user-title">{finalMode === 'edit' ? 'Edit User' : 'Add New User'}</h2>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">First Name</label>
              <Input
                type="text"
                placeholder="Enter first name"
                value={formData.firstName}
                onChange={(e) => handleInputChange('firstName', e.target.value)}
                className={errors.firstName ? 'form-input error' : 'form-input'}
              />
              {errors.firstName && <span className="error-message">{errors.firstName}</span>}
            </div>
            <div className="form-group">
              <label className="form-label">Last Name</label>
              <Input
                type="text"
                placeholder="Enter last name"
                value={formData.lastName}
                onChange={(e) => handleInputChange('lastName', e.target.value)}
                className={errors.lastName ? 'form-input error' : 'form-input'}
              />
              {errors.lastName && <span className="error-message">{errors.lastName}</span>}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Email</label>
              <Input
                type="email"
                placeholder="Enter email address"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className={errors.email ? 'form-input error' : 'form-input'}
              />
              {errors.email && <span className="error-message">{errors.email}</span>}
            </div>
            <div className="form-group">
              <label className="form-label">Phone</label>
              <Input
                type="tel"
                placeholder="Enter phone number"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                className="form-input"
              />
            </div>
          </div>

          <hr className="form-divider" />

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Organization</label>
              <p className="field-description">Select the organization that the user belongs to</p>
              <Select
                value={formData.organization}
                onChange={(e) => handleInputChange('organization', e.target.value)}
                options={[
                  { value: '', label: 'Select organization' },
                  ...organizations.map(org => ({ value: String(org.id), label: org.name }))
                ]}
                className={errors.organization ? 'form-input error' : 'form-input'}
              />
              {errors.organization && <span className="error-message">{errors.organization}</span>}
            </div>
            <div className="form-group role-field">
              <label className="form-label">Role</label>
              <p className="field-description">The user's role within the organization</p>
              <Select
                value={formData.role}
                onChange={(e) => handleInputChange('role', e.target.value)}
                options={[
                  { value: '', label: 'Select a role' },
                  ...roles.map(role => ({ value: String(role.id), label: role.name }))
                ]}
                className={errors.role ? 'form-input error' : 'form-input'}
              />
              {errors.role && <span className="error-message">{errors.role}</span>}
            </div>
          </div>

          <hr className="form-divider" />

          <label className="form-label">Incubation Access</label>
          <p className="section-note">Check an incubation to grant access, then pick the specific permissions for that incubation.</p>

          <div className="checkbox-group">
            {incubations.length === 0 ? (
              <div className="no-incubations"><p>No incubations available</p></div>
            ) : (
              incubations.map((incubation) => {
                const incubationId = String(incubation.id);
                const isGranted = Object.prototype.hasOwnProperty.call(formData.incubationAccessMap, incubationId);
                return (
                  <div key={incubation.id} className="incubation-row">
                    <div className="checkbox-item">
                      <Checkbox
                        checked={isGranted}
                        onChange={(e) => toggleIncubation(incubationId, e.target.checked)}
                        label={incubation.name || incubation.title || `Incubation ${incubation.id}`}
                      />
                    </div>
                    {isGranted && incubationFunctionalities.length > 0 && (
                      <>
                        <p className="incubation-permissions-header">
                          {`Select this user's access level within the ${incubation.name || incubation.title || `Incubation ${incubation.id}`} incubation`}
                        </p>
                        <div className="incubation-permissions">
                          {incubationFunctionalities.map(func => renderIncubationPermissionCheckbox(incubationId, func))}
                        </div>
                      </>
                    )}
                  </div>
                );
              })
            )}
          </div>

          <hr className="form-divider" />

          <div className="dashboard-permissions-header">
            <div className="dashboard-permissions-heading">
              <label className="form-label">Enable Dashboard Permissions</label>
              <p className="field-description">Allow this user to manage the Fetchboard platform</p>
            </div>
            <Switch
              checked={dashboardPermissionsEnabled}
              onChange={(e) => handleToggleDashboardPermissions(e.target.checked)}
            />
          </div>
          {dashboardPermissionsEnabled && (
            <>
              {!currentUserIsAdmin && (
                <p className="section-note">Only current admins can grant the Admin permission.</p>
              )}
              <div className="functionalities-container">
                {globalFunctionalities.length > 0 ? (
                  <div className="global-functionalities-list">
                    {globalFunctionalities.map(renderGlobalCheckbox)}
                  </div>
                ) : (
                  <div className="no-functionalities"><p>No dashboard permissions available.</p></div>
                )}
              </div>
            </>
          )}

          <hr className="form-divider" />

          <div className="form-actions">
            <Button variant="outline" size="medium" className="action-button" onClick={handleBackClick}>
              Cancel
            </Button>
            <Button variant="primary" size="medium" className="action-button" onClick={handleSubmit} ripple disabled={submitting}>
              {submitting
                ? (finalMode === 'edit' ? 'Updating...' : 'Creating...')
                : (finalMode === 'edit' ? 'Update User' : 'Create User')}
            </Button>
          </div>
        </Card>
      </div>
      <div style={{ height: '300px' }}></div>
    </div>
  );
};

export default AddUserScreen;
